import React from 'react';
import { validated } from 'react-custom-validation'
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import moment from 'moment';
import { CardElement, injectStripe } from "react-stripe-elements";

import { isEmail, isEqual, isRequired } from 'utils/validations';
import { handleTextChange } from 'utils/handle-changes';

import Button from 'components/Button';
import Form from 'components/Form';
import FormValidationMessage from 'components/FormValidationMessage';
import FormRow from 'components/FormRow';
import H3 from 'components/H3';
import Label from 'components/Label';
import LoadingCircle from 'components/LoadingCircle';
import Modal from 'components/Modal';
import Textbox from 'components/Textbox';

const plans = {
    annual: 'Annual'
};

class AgencySignupForm extends React.Component {
    static propTypes = {
        company: PropTypes.object.isRequired,
        error: PropTypes.object.isRequired,
        handleSubmit: PropTypes.func.isRequired,
        isSaving: PropTypes.bool.isRequired,
        setCompany: PropTypes.func.isRequired,
        setUser: PropTypes.func.isRequired,
        user: PropTypes.object.isRequired
    };

    constructor() {
        super();

        this.state = {
            showConfirmationModal: false,
            isGettingToken: false,
            promoCode: '',
            ccError: null
        };
    }

    _handleSubmit = async () => {
        this.setState({isGettingToken: true, ccError: null});
        let { token } = await this.props.stripe.createToken();
        console.log(token);
        if(!token) {
            this.setState({ccError: 'There was a problem with your credit card information. Please double-check what you have entered and try again.', isGettingToken: false, showConfirmationModal: false});
            return;
        }
        this.props.handleSubmit(token.id, this.state.promoCode)
            .then((res) => {
                this.setState({isGettingToken: true});
                this.props.$fieldEvent('reset');
                this.setState({showConfirmationModal: false, promoCode: ''});
            })
            .catch((err) => {
                this.setState({isGettingToken: false, showConfirmationModal: false});
            });
    };

    _handleConfirm(isValid) {
        if(isValid === true) {
            this.setState({showConfirmationModal: true});
        }
    }

    handleCompanyChange = (name, value) => {
        let newCompanyObj = update(this.props.company, {});
        newCompanyObj[name] = value;
        this.props.setCompany(newCompanyObj);
    };

    handleUserChange = (name, value) => {
        let newUserObj = update(this.props.user, {});
        newUserObj[name] = value;
        this.props.setUser(newUserObj);
    };

    render() {
        let { company, error, isSaving, user, $field, $validation } = this.props;

        return (
            <Form className="auth-form">
                <H3>
                    Create Your Member Account
                </H3>

                <FormRow>
                    Plan Selected: <strong>{plans[company.subscriptionTier]}</strong>
                </FormRow>

                <FormRow>
                    <Textbox
                        id="txtFirstName"
                        label="First Name"
                        name="firstName"
                        required
                        type="text"
                        validation={$validation.firstName}
                        value={user.firstName || ''}
                        {...$field('firstName', (event) => handleTextChange(this.handleUserChange, event))}
                    />
                </FormRow>

                <FormRow>
                    <Textbox
                        id="txtLastName"
                        label="Last Name"
                        name="lastName"
                        required
                        type="text"
                        validation={$validation.lastName}
                        value={user.lastName || ''}
                        {...$field('lastName', (event) => handleTextChange(this.handleUserChange, event))}
                    />
                </FormRow>

                <FormRow>
                    <Textbox
                        icon="email"
                        id="txtEmailAddress"
                        label="Email"
                        name="email"
                        required
                        type="email"
                        validation={$validation.email}
                        value={user.email || ''}
                        {...$field('email', (event) => handleTextChange(this.handleUserChange, event))}
                    />
                </FormRow>

                <FormRow>
                    <Textbox
                        id="txtCompanyName"
                        label="Business Name"
                        name="name"
                        required
                        type="text"
                        validation={$validation.name}
                        value={company.name || ''}
                        {...$field('name', (event) => handleTextChange(this.handleCompanyChange, event))}
                    />
                </FormRow>

                <FormRow>
                    <Textbox
                        id="txtPassword"
                        label="Password"
                        name="password"
                        required
                        type="password"
                        validation={$validation.password}
                        value={user.password || ''}
                        {...$field('password', (event) => handleTextChange(this.handleUserChange, event))}
                    />
                </FormRow>

                <FormRow>
                    <Textbox
                        id="txtPassword2"
                        label="Confirm Password"
                        name="password2"
                        required
                        type="password"
                        validation={$validation.password2}
                        value={user.password2 || ''}
                        {...$field('password2', (event) => handleTextChange(this.handleUserChange, event))}
                    />
                </FormRow>

                <FormRow>
                    <Label>Payment <span className="c-label__required-asterisk">*</span></Label>

                    <div className="stripe-wrapper">
                        <CardElement />
                    </div>
                </FormRow>

                <FormRow>
                    <Textbox
                        id="txtPromoCode"
                        label="Have a Promo Code?"
                        name="promoCode"
                        onChange={(event) => this.setState({promoCode: event.target.value})}
                        type="text"
                        value={this.state.promoCode || ''}
                    />
                </FormRow>

                {Object.keys(error).length > 0 ? (
                    <FormRow className="error-message">
                        <FormValidationMessage>
                            {error.friendlyMessage || error.message}
                        </FormValidationMessage>
                    </FormRow>
                ) : null}

                {this.state.ccError && this.state.ccError.length > 0 ? (
                    <FormRow className="error-message">
                        <FormValidationMessage>
                            {this.state.ccError}
                        </FormValidationMessage>
                    </FormRow>
                ) : null}

                <FormRow className="button-wrapper">
                    <Button className="a-button--blue"
                            disabled={isSaving === true || this.state.isGettingToken}
                            onClick={(event) => {
                                event.preventDefault();
                                this.props.$submit(this._handleConfirm.bind(this, true), this._handleConfirm.bind(this, false));
                            }}
                            type="submit">
                        Send
                    </Button>
                </FormRow>

                <Modal show={this.state.showConfirmationModal === true}
                       title="The Hunt Agency Subscription Agreement">
                    <div className="subscription-agreement">
                        <p>This Subscription Agreement (“<strong>Agreement</strong>”) is entered into by and between {user.firstName} {user.lastName} (“<strong>Customer</strong>”) and The Hunt Agency, LLC (“<strong>The Hunt Agency</strong>”) (each a “Party” and together the “Parties”) on {moment().format('MMMM DD, YYYY')} (the “<strong>Effective Date</strong>”)..</p>

                            <ul>
                                <li>Customer wishes to use The Hunt Agency website located at thehunt.agency (“Website”) to source freelancer talent in the advertising and marketing industry.</li>
                                <li>This Agreement is to clarify the mutual promises the Parties have made regarding Customer’s subscription to the services described herein.</li>
                            </ul>

                        <p>The Parties agree as follows:</p>

                        <ol>
                            <li>
                                <strong>24-Hour Trial Period.</strong> Customer is entitled to a 24-hour trial period of its selected Plan 	(as defined below). Customer must provide credit card information prior to the commencement of the 24-hour trial period and, should Customer not cancel prior to the expiration of the 24-hour period, Customer’s card will be charged an amount equal to the annual rate of the Plan. This Agreement will begin on the Effective Date and will automatically renew on the same day of the following year as the Effective Date every year until termination of the Plan. Customer may cancel the Plan at any time; however, any cancellation after payment has been made will not terminate either Party’s obligation under this Agreement until the following yearly anniversary of the Effective Date.
                            </li>
                            <li>
                                <strong>SERVICES.</strong> Upon signing this Agreement, Customer will be entitled to use the Website and have access to view the profiles for the pool of freelancers (“Services”). Customer may log-on and use the Website on an unlimited basis during the term of the Plan. This Agreement is made in addition to, and not in abrogation of, the Website Terms & Conditions and Privacy Policy, which are incorporated herein by reference. In the event of a conflict between this Agreement and the Terms & Conditions or Privacy Policy, this Agreement will control.
                            </li>
                            <li>
                                <strong>PAYMENT.</strong> Customer must provide a credit card upon the commencement of this Agreement (the “<strong>Card</strong>”). The Hunt Agency is authorized to automatically charge The Card for all fees payable by Customer under this Agreement on the yearly anniversary of the Effective Date unless the Plan is cancelled in accordance with Section 7 or the Customer suspends its account
                            </li>
                            <li>
                                <strong>CUSTOMER OBLIGATIONS.</strong> The Website and Services provided by The Hunt Agency are only for the specific Customer’s internal and non-commercial use, and may not be transferred, resold or licensed to any third party. Customer must not use the Website or Services for any improper or illegal purpose.
                            </li>
                            <li>
                                <strong>CANCELLATION.</strong> Each Party must comply with this Agreement in full through the last day of the month in which the Plan is cancelled.
                            </li>
                            <li>
                                <strong>LIMITATION OF LIABILITY.</strong> The Website, Services, Plan, and all other aspects of this Agreement are provided on an “AS IS” and “AS AVAILABLE” basis. The Hunt Agency is not responsible for any damages of any kind related to Customer’s use of the Website and/or Services.
                            </li>
                            <li>
                                <strong>INDEMNIFICATION.</strong> Customer agrees to defend, indemnify, and hold harmless The Hunt Agency (and its officers, owners, agents, employees, affiliates, and assigns) from and against all “Claims” (as defined below) to which The Hunt Agency may be subjected, to the extent that such Claims arise out of Customer’s actions or omissions respective to performance of this Agreement. A “Claim” under this paragraph refers to any damages, liabilities, costs, suits, penalties, obligations, or other third-party claim of rights, including reasonable attorneys’ fees that may be asserted as part of the Claim.
                            </li>
                            <li>
                                <strong>ASSIGNMENT.</strong> Customer’s rights and obligations hereunder are not transferrable or assignable, either directly or as a result of a merger or other business combination and are specific to the division of Customer’s company that contracted for the Services and not transferrable beyond such division.
                            </li>
                            <li>
                                <strong>NOTICE.</strong> All notices provided for in this Agreement must be in writing and will be deemed to have been duly given if delivered personally, sent by registered or certified mail, return receipt requested and postage prepaid, or sent via electronic mail where receipt by the receiving party is confirmed by the sending party.
                            </li>
                            <li>
                                <strong>GOVERNING LAW.</strong> This Agreement is governed by the laws of the State of Minnesota, without regard to its conflicts of laws provisions. The Parties hereby submit to the exclusive personal jurisdiction and venue of the courts located in Hennepin County, Minnesota.
                            </li>
                            <li>
                                <strong>NO WAIVER.</strong> No Party will be deemed to have waived any rights, power, or privilege under this Agreement unless such waiver is in writing signed by the waiving party.
                            </li>
                            <li>
                                <strong>SEVERABILITY.</strong> If any part of this Agreement is deemed to be invalid or unenforceable by a court of competent jurisdiction, the remaining provisions of this Agreement will remain binding and in full force and effect.
                            </li>
                            <li>
                                <strong>MODIFICATION.</strong> This Agreement may only be amended or modified by written agreement of the Parties.
                            </li>
                        </ol>
                    </div>

                    <div className="c-modal__button-wrapper">
                        <Button disabled={isSaving === true || this.state.isGettingToken}
                                onClick={() => {
                                    this._handleSubmit();
                                }}>
                            I Accept

                            {isSaving === true || this.state.isGettingToken ? <LoadingCircle innerColor="#ffffff" outerColor="#ffffff" /> : null}
                        </Button>
                        <Button className="a-button--cancel"
                                disabled={isSaving === true || this.state.isGettingToken}
                                onClick={() => {
                                    this.setState({showConfirmationModal: false});
                                }}>
                            Cancel
                        </Button>
                    </div>
                </Modal>
            </Form>
        )
    }
}

function signupFormValidationConfig(props) {
    const { email, firstName, lastName, password, password2 } = props.user;
    const { name } = props.company;

    return {
        fields: ['email', 'firstName', 'lastName', 'name', 'password', 'password2'],
        validations: {
            email: [
                [isRequired, email],
                [isEmail, email]
            ],
            firstName: [
                [isRequired, firstName]
            ],
            lastName: [
                [isRequired, lastName]
            ],
            name: [
                [isRequired, name]
            ],
            password: [
                [isRequired, password]
            ],
            password2: [
                [isRequired, password2],
                [isEqual, password, password2, 'Passwords must match']
            ]
        }
    }
}

export default AgencySignupForm = validated(signupFormValidationConfig)(injectStripe(AgencySignupForm));



// WEBPACK FOOTER //
// ./src/containers/Auth/Signup/AgencySignupPage/AgencySignupForm.js